import { HourReportWorkflow, useGetHourReportSettingsQuery } from "../../organisations/api"
import { canUpdateHourReportSettings, type HourReportSettings } from "../../organisations/api/hour-report-settings"

export type HourReportingWorkflowSettings = {
  workflow: HourReportWorkflow
  isReportingOnlyWorkflow: boolean
  isApprovalWorkflow: boolean
  isCheckAndApprovalWorkflow: boolean
  supportsSubmission: boolean
  supportsChecking: boolean
  supportsApproval: boolean
  reviewRequiresSubmission: boolean
  approvalRequiresChecking: boolean
  allowAdminsToCheckAndApproveOwnHours: boolean
}

export type HourReportingWorkflowSettingsProps = {
  workflowSettings?: HourReportingWorkflowSettings
}

export const makeHourReportingWorkflowSettings = (settings: HourReportSettings): HourReportingWorkflowSettings => {
  const isReportingOnlyWorkflow = settings.workflow === HourReportWorkflow.ReportingOnly
  const isApprovalWorkflow = settings.workflow === HourReportWorkflow.Approval
  const isCheckAndApprovalWorkflow = settings.workflow === HourReportWorkflow.CheckAndApproval

  return {
    workflow: settings.workflow,
    isReportingOnlyWorkflow,
    isApprovalWorkflow,
    isCheckAndApprovalWorkflow,
    supportsSubmission: !isReportingOnlyWorkflow,
    supportsChecking: isCheckAndApprovalWorkflow,
    supportsApproval: isApprovalWorkflow || isCheckAndApprovalWorkflow,
    reviewRequiresSubmission: !isReportingOnlyWorkflow && Boolean(settings.reviewRequiresSubmission),
    approvalRequiresChecking: isCheckAndApprovalWorkflow && Boolean(settings.approvalRequiresChecking),
    allowAdminsToCheckAndApproveOwnHours: settings.allowAdminsToCheckAndApproveOwnHours,
  }
}

const PERSONAL_HOUR_REPORTING_WORKFLOW_SETTINGS: HourReportingWorkflowSettings = makeHourReportingWorkflowSettings({
  workflow: HourReportWorkflow.ReportingOnly,
  reviewRequiresSubmission: false,
  approvalRequiresChecking: false,
  allowAdminsToCheckAndApproveOwnHours: false,
})

export const useHourReportingWorkflowSettings = (orgId: string) => {
  const { data, ...queryState } = useGetHourReportSettingsQuery(orgId, { skip: !orgId })

  return {
    settings: data
      ? makeHourReportingWorkflowSettings(data.hourReportSettings)
      : queryState.isLoading
      ? undefined
      : PERSONAL_HOUR_REPORTING_WORKFLOW_SETTINGS,
    canUpdateSettings: canUpdateHourReportSettings(data),
    ...queryState,
  }
}
